import { Injectable, inject } from '@angular/core';
import { Clause } from '../_models/clause';
import { InfoDocument } from '../_models/info-document';
import { HttpClient } from '@angular/common/http';
import { EventService } from './event.service';
import { DialogService } from './dialog.service';
import { environment } from '../../environments/environment';
import {
  Observable,
  Subject,
  catchError,
  delay,
  forkJoin,
  map,
  of,
  retry,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { Task } from '../_models/task';
import { AmlSseEventEnum, AmlTaskEnum } from '../_models/aml-task.enum';
import { RequestBody } from '../_models/request-body';
import { ProcessStatusEnum } from '../_models/proces-status.enum';
import { v4 as uuidv4 } from 'uuid';
import { LoaderService } from './loader.service';
import { SpidSseEventEnum, SpidTaskEnum } from '../_models/spid-task.enum';

@Injectable({
  providedIn: 'root',
})
export class AmlService {
  clausesCustomer?: Clause[];
  clausesCustomerSign?: InfoDocument[];
  signedDocuments?: any[];
  serialNumber?: string;
  processId: string = '';
  idPratica: string = '';
  taskRecord: Partial<
    Record<AmlTaskEnum | SpidTaskEnum, Task<AmlTaskEnum | SpidTaskEnum>>
  > = {};
  isLoading = new Subject<boolean>();

  nextTask: Task<AmlTaskEnum | SpidTaskEnum> | undefined;

  private http = inject(HttpClient);
  private eventService = inject(EventService);
  private dialogService = inject(DialogService);

  startProcess(processId: string) {
    this.processId = processId;
    return this.getActiveTask();
  }

  getActiveTask(): Observable<Task<AmlTaskEnum | SpidTaskEnum> | undefined> {
    const url = `${environment.api}/camunda/sign/v1/task/${this.processId}`;
    return this.http
      .get<{ tasks: Task<AmlTaskEnum | SpidTaskEnum>[] }>(url)
      .pipe(
        map((res) => {
          if (!res || !res.tasks || res.tasks.length === 0) {
            throw res;
          }
          return res;
        }),
        retry({
          count: 5,
          delay: (error, retryCount) => {
            this.isLoading.next(true);
            console.log(
              `Attempt ${retryCount}: retrying in ${retryCount * 1000}ms`
            );
            return timer(retryCount * 1000);
          },
        }),
        catchError((err) => {
          this.isLoading.next(false);
          if (err.tasks && Array.isArray(err.tasks) && err.tasks.length === 0) {
            return of({ tasks: [] as Task<AmlTaskEnum | SpidTaskEnum>[] });
          }
          return throwError(() => 'Errore durante il recupero del task');
        }),
        map((res) => {
          this.isLoading.next(false);
          let taskRecord: Partial<
            Record<AmlTaskEnum | SpidTaskEnum, Task<AmlTaskEnum | SpidTaskEnum>>
          > = {};
          res.tasks.forEach((task) => (taskRecord[task.name] = task));
          return taskRecord;
        }),

        tap((res) => {
          this.taskRecord = res;
          this.nextTask = this.getNextTask(this.taskRecord);
        }),
        map(() => this.nextTask)
      );
  }

  private getNextTask(
    taskRecord: Partial<
      Record<AmlTaskEnum | SpidTaskEnum, Task<AmlTaskEnum | SpidTaskEnum>>
    >
  ) {
    let taskExecutionOrder: (AmlTaskEnum | SpidTaskEnum)[] = [
      AmlTaskEnum.SIGN_OR_SEND,
      AmlTaskEnum.START_PROCESS,
      AmlTaskEnum.ONESHOT_REGISTER,
      AmlTaskEnum.SIGN,
      SpidTaskEnum.START_PROCESS,
      SpidTaskEnum.ONESHOT_REGISTER,
      SpidTaskEnum.SIGN,
    ];
    let taskToBeExecuted: Task<AmlTaskEnum | SpidTaskEnum> | undefined;
    for (let task of taskExecutionOrder) {
      if (taskRecord[task]?.id) {
        taskToBeExecuted = taskRecord[task];
        break;
      }
    }
    return taskToBeExecuted;
  }

  completeTask<R = object, E = any>(
    task?: Partial<Task<AmlTaskEnum | SpidTaskEnum>> & { id: string },
    body?: Omit<RequestBody, 'subscriber'>,
    url = `${environment.api}/camunda/sign/v1/task/${task?.id}`
  ) {
    if (!task) {
      this.dialogService.onError('Task mancante');
      return throwError(() => 'Task mancante');
    }
    const requestBody = this.getRequestBody(body);
    let ok: SpidSseEventEnum | AmlSseEventEnum,
      ko: SpidSseEventEnum | AmlSseEventEnum,
      warning: SpidSseEventEnum | AmlSseEventEnum;
    if (task.name?.includes('SPIDID')) {
      ok = SpidSseEventEnum.OK;
      ko = SpidSseEventEnum.KO;
      warning = SpidSseEventEnum.WARNING;
    } else {
      ok = AmlSseEventEnum.OK;
      ko = AmlSseEventEnum.KO;
      warning = AmlSseEventEnum.WARNING;
    }
    return forkJoin([
      this.subscribeSse<R, E>(requestBody.subscriber, ok, ko, warning),
      this.http.post(url, requestBody),
    ]).pipe(
      map(([res]) => res),
      catchError((err) => {
        console.error(err);
        this.isLoading.next(false);
        let errorMessage = err.errorMessage;
        if (typeof err === 'string') {
          errorMessage = err;
        }
        const isWrongOtp = errorMessage?.includes('wrong OTP');
        if (isWrongOtp) {
          errorMessage = 'OTP errato, si prega di riprovare';
        }
        this.dialogService.onError(
          errorMessage || "Errore durante l'esecuzione del task"
        );

        return throwError(() => errorMessage);
      })
    );
  }

  private getRequestBody(value?: Omit<RequestBody, 'subscriber'>) {
    let subscriber = uuidv4();
    let requestBody: RequestBody = {
      ...(value || {}),
      subscriber,
    };
    return requestBody;
  }

  private subscribeSse<R = object, E = any>(
    subscriberId: string,
    successEventName: SpidSseEventEnum | AmlSseEventEnum,
    errorEventName: SpidSseEventEnum | AmlSseEventEnum,
    warningEventName: SpidSseEventEnum | AmlSseEventEnum
  ): Observable<R | undefined> {
    this.isLoading.next(true);
    return this.eventService
      .getServerEvent<R, E>(
        subscriberId,
        successEventName,
        errorEventName,
        warningEventName
      )
      .pipe(
        tap((res) => {
          this.isLoading.next(false);
        })
      );
  }

  getPath(task: Task<AmlTaskEnum | SpidTaskEnum> | undefined) {
    if (
      task?.name === AmlTaskEnum.ONESHOT_REGISTER ||
      task?.name === SpidTaskEnum.ONESHOT_REGISTER
    ) {
      return [
        `${this.idPratica}/process/${this.processId}/oneshot-register/${task?.id}`,
      ];
    } else if (
      task?.name === AmlTaskEnum.SIGN ||
      task?.name === SpidTaskEnum.SIGN
    ) {
      return [
        `${this.idPratica}/process/${this.processId}/issue-sign/${task?.id}`,
      ];
    } else {
      return [`error`];
    }
  }

  nextAction(
    task: Task<AmlTaskEnum | SpidTaskEnum> | undefined,
    body?: Omit<RequestBody, 'subscriber'>
  ): Observable<Task<AmlTaskEnum | SpidTaskEnum> | undefined> {
    if (!task) {
      return throwError(() => 'No task found');
    }
    if (
      task?.name !== AmlTaskEnum.START_PROCESS &&
      task?.name !== SpidTaskEnum.START_PROCESS
    ) {
      return this.completeTask(task, body).pipe(
        switchMap((res) => this.getActiveTask()),
        switchMap((res) => this.nextAction(res))
      );
    } else {
      return this.completeTask<{
        status: ProcessStatusEnum;
        person?: { serialNumber: string };
        clauses?: Clause[];
      }>(task).pipe(
        tap((res) => {
          this.serialNumber = res?.person?.serialNumber;
          this.clausesCustomer = res?.clauses;
        }),
        switchMap((res) => this.getActiveTask())
      );
    }
  }

  resendOtp() {
    const otpTask =
      this.taskRecord[AmlTaskEnum.RESEND_OTP] ||
      this.taskRecord[SpidTaskEnum.RESEND_OTP];
    if (!otpTask) {
      return throwError(() => 'No task found');
    }
    this.isLoading.next(true);
    const url = `${environment.api}/camunda/sign/v1/task/${otpTask.id}`;
    return this.http.post(url, {}).pipe(
      delay(2000),
      switchMap(() => this.getActiveTask()),
      tap(
        () => {
          this.isLoading.next(false);
        },
        (error) => {
          this.isLoading.next(false);
        }
      )
    );
  }

  resetAll() {
    this.resetProcess();
    this.idPratica = '';
  }

  resetProcess() {
    this.clausesCustomer = undefined;
    this.clausesCustomerSign = undefined;
    this.signedDocuments = undefined;
    this.serialNumber = undefined;
    this.processId = '';
    this.taskRecord = {};
    this.nextTask = undefined;
  }
}
